import * as Sentry from '@sentry/react';
import axios from 'axios';
import axiosRetry from 'axios-retry';

//App Settings
import { NetworkSettings } from '../utils';
import { AuthService } from 'services/AuthService';
import { reportSessionExpired } from 'utils/sessionExpiry';

//* Shared by every API client. A 401 means the session is gone: the first one starts the expiry
//* flow (logout, then a reload into Login), and every 401 it owns is left pending instead of
//* rejected, so callers don't stack their own error toasts on top while the page reloads.
//* Public endpoints authenticate with the API key, so their 401s say nothing about the session.
const handleResponseError = error => {
  if (error.response?.status === 401) {
    if (!error.config?.url?.includes?.('public/') && reportSessionExpired()) {
      return new Promise(() => {});
    }
  } else {
    Sentry.captureException(error);
  }
  return Promise.reject(error);
};

const RequestApi = axios.create({
  baseURL: NetworkSettings.fullApiUrl,
  headers: {
    'Content-Type': 'application/json',
    'Source-App': 'Freedom-Worker-Portal',
    'Accept-Language': 'en',
  },
});

axiosRetry(RequestApi, {
  retries: 0, // number of retries
  retryDelay: retryCount => {
    return retryCount * 2000; // time interval between retries
  },
  retryCondition: (error: any) => {
    // if retry condition is not specified, by default idempotent requests are retried
    return error.response.status !== 409 && error.response.status != 401;
  },
});

RequestApi.interceptors.request.use(
  async config => {
    if (config.url?.includes?.('public/')) {
      config.headers!['X-Api-Key'] = process.env.REACT_APP_X_API_KEY;
    } else {
      const session = await AuthService.getSessionSafe();
      if (session) {
        const jwt = session.getIdToken?.()?.getJwtToken?.();
        if (jwt) {
          config.headers!['Authorization'] = `Bearer ${jwt}`;
        }
      } else {
        config.headers!['X-Api-Key'] = process.env.REACT_APP_X_API_KEY;
      }
    }
    return config;
  },
  error => Promise.reject(error),
);

RequestApi.interceptors.response.use(response => response, handleResponseError);

const securityApi = axios.create({
  baseURL: NetworkSettings.fullSecurityApiUrl,
  headers: {
    'Content-Type': 'application/json',
    'Source-App': 'Freedom-Worker-Portal',
  },
});

securityApi.interceptors.request.use(
  async config => {
    if (config.url?.includes?.('public/')) {
      config.headers!['X-Api-Key'] = process.env.REACT_APP_X_API_KEY;
    } else {
      const session = await AuthService.getSessionSafe();
      if (session) {
        const jwt = session.getIdToken?.()?.getJwtToken?.();
        if (jwt) {
          config.headers!['Authorization'] = `Bearer ${jwt}`;
        }
      } else {
        config.headers!['X-Api-Key'] = process.env.REACT_APP_X_API_KEY;
      }
    }
    return config;
  },
  error => Promise.reject(error),
);

securityApi.interceptors.response.use(response => response, handleResponseError);

const learningApi = axios.create({
  baseURL: NetworkSettings.fullLearningApiUrl,
  headers: {
    'Content-Type': 'application/json',
    'Source-App': 'Freedom-Worker-Portal',
  },
});

learningApi.interceptors.request.use(
  async config => {
    if (config.url?.includes?.('public/')) {
      config.headers!['X-Api-Key'] = process.env.REACT_APP_X_API_KEY;
    } else {
      const session = await AuthService.getSessionSafe();
      if (session) {
        const jwt = session.getIdToken?.()?.getJwtToken?.();
        if (jwt) {
          config.headers!['Authorization'] = `Bearer ${jwt}`;
        }
      } else {
        config.headers!['X-Api-Key'] = process.env.REACT_APP_X_API_KEY;
      }
    }
    return config;
  },
  error => Promise.reject(error),
);

learningApi.interceptors.response.use(response => response, handleResponseError);
export { RequestApi, securityApi, learningApi };
